import { z } from 'zod';

/**
 * The public API (/v1): the WordPress plugin, n8n and other tools use it with an
 * agency's API key. Each key has permissions, shown with these labels.
 */
export const API_KEY_PERMISSIONS = {
  'catalog.read': {
    label: 'Leer el catálogo',
    help: 'Propiedades publicadas en la web, sin datos privados.',
  },
  'leads.create': {
    label: 'Crear leads',
    help: 'Formularios del sitio web y leads de Meta (n8n).',
  },
} as const;
export type ApiKeyPermission = keyof typeof API_KEY_PERMISSIONS;

export const apiKeyCreateSchema = z.strictObject({
  name: z.string().trim().min(1, 'Ponle un nombre a la clave').max(80),
  permissions: z
    .array(z.enum(Object.keys(API_KEY_PERMISSIONS) as [ApiKeyPermission]))
    .min(1, 'Elige al menos un permiso')
    .transform((list) => [...new Set(list)]),
});
export type ApiKeyCreateInput = z.input<typeof apiKeyCreateSchema>;

/** "La Unión" → "la-union", "Cabañas" → "cabanas": the website's URL slugs. */
export function placeSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const flag = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true')
  .optional();
const amount = z.coerce.number().min(0).max(1e12).optional();

/**
 * GET /v1/properties: published on the website only. Areas in m² (whatever each
 * property's unit). `lang=en` lists only properties with an English title.
 */
export const catalogQuerySchema = z.strictObject({
  lang: z.enum(['es', 'en']).default('es'),
  operation: z.enum(['sale', 'rent']).optional(),
  type: z.enum(['house', 'apartment', 'land', 'commercial', 'office', 'warehouse']).optional(),
  department: z
    .string()
    .regex(/^[a-z0-9-]{1,40}$/)
    .optional(),
  district: z.coerce.number().int().positive().optional(),
  q: z.string().trim().max(100).optional(),
  /**
   * A place by name (the website's "Ciudad"): zone, district, municipality or
   * department. Public data only: never the exact address.
   */
  place: z.string().trim().min(1).max(100).optional(),
  priceMin: amount,
  priceMax: amount,
  bedroomsMin: z.coerce.number().int().min(0).max(50).optional(),
  bathroomsMin: z.coerce.number().min(0).max(50).optional(),
  parkingMin: z.coerce.number().int().min(0).max(500).optional(),
  builtAreaMin: amount,
  builtAreaMax: amount,
  lotAreaMin: amount,
  lotAreaMax: amount,
  featured: flag,
  sort: z.enum(['recent', 'price_asc', 'price_desc']).default('recent'),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24),
});
export type CatalogQuery = z.output<typeof catalogQuerySchema>;

/**
 * POST /v1/leads (leads.create): a lead from the website's forms (WordPress plugin)
 * or Meta's lead forms (n8n). Phone or email, and the visitor's consent, are required.
 * `externalId` makes retries safe (the same lead is not created twice).
 */
export const LEAD_CHANNELS = { website: 'Sitio web', meta: 'Meta' } as const;
export type LeadChannel = keyof typeof LEAD_CHANNELS;

const leadText = (max: number) => z.string().trim().max(max).optional();

export const inboundLeadSchema = z
  .strictObject({
    /** inquiry: buy or rent (or a question); owner: wants to sell or rent their property. */
    kind: z.enum(['inquiry', 'owner']).default('inquiry'),
    name: z.string().trim().min(1, 'Falta el nombre').max(120),
    email: z.email('Correo no válido').max(254).optional(),
    phone: z.string().trim().min(7).max(30).optional(),
    message: leadText(2000),
    /** The property the visitor asked about (its public code). */
    propertyCode: z
      .string()
      .regex(/^P[2-9A-HJ-NP-Z]{6}$/, 'Código de propiedad no válido')
      .optional(),
    /** What the visitor wants, when the form asks (inquiries). */
    interest: z.enum(['buy', 'rent', 'sell', 'advice']).optional(),
    /** The owner's form ("Vender"). */
    owner: z
      .strictObject({
        operation: z.enum(['sale', 'rent']).optional(),
        propertyType: leadText(40),
        department: leadText(60),
        location: leadText(200),
        expectedPrice: leadText(60),
      })
      .optional(),
    channel: z.enum(Object.keys(LEAD_CHANNELS) as [LeadChannel]).default('website'),
    /** Which form (contacto, modal, vender…; for Meta, the form's name). */
    form: leadText(60),
    page: z.url().max(500).optional(),
    language: z.enum(['es', 'en']).default('es'),
    utm: z
      .strictObject({
        source: leadText(100),
        medium: leadText(100),
        campaign: leadText(100),
        term: leadText(100),
        content: leadText(100),
      })
      .optional(),
    consent: z.strictObject({
      accepted: z.literal(true, { message: 'Falta el consentimiento del visitante' }),
      at: z.iso.datetime({ offset: true }),
      privacyUrl: z.url().max(500).optional(),
    }),
    /** The visitor's IP (the plugin forwards it): per-visitor limit, stored only as HMAC. */
    visitorIp: z.union([z.ipv4(), z.ipv6()]).optional(),
    externalId: leadText(100),
  })
  .refine((v) => Boolean(v.email ?? v.phone), {
    path: ['phone'],
    message: 'Hace falta un teléfono o un correo',
  });
export type InboundLeadInput = z.output<typeof inboundLeadSchema>;

/** POST /v1/events/whatsapp-click: a click on the website's WhatsApp button. */
export const whatsappClickSchema = z.strictObject({
  propertyCode: z
    .string()
    .regex(/^P[2-9A-HJ-NP-Z]{6}$/)
    .optional(),
});

// ---------------------------------------------------------------------------
// Lead routing (HU-CRM-03): who handles each lead and what happens without an answer
// ---------------------------------------------------------------------------

/** ISO weekdays, as tenants.business_hours keys ("1" = Monday). */
export const WEEKDAYS = {
  '1': 'Lunes',
  '2': 'Martes',
  '3': 'Miércoles',
  '4': 'Jueves',
  '5': 'Viernes',
  '6': 'Sábado',
  '7': 'Domingo',
} as const;
export type Weekday = keyof typeof WEEKDAYS;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora no válida (HH:MM)');

/** Per weekday: [opens, closes]; a missing day is closed. */
export const businessHoursSchema = z
  .partialRecord(z.enum(Object.keys(WEEKDAYS) as [Weekday]), z.tuple([hhmm, hhmm]))
  .refine((hours) => Object.values(hours).every(([opens, closes]) => opens < closes), {
    message: 'La hora de cierre debe ser después de la de apertura',
  });
export type BusinessHoursInput = z.infer<typeof businessHoursSchema>;

/** PATCH /settings/leads. */
export const leadSettingsSchema = z.strictObject({
  /** Receives leads no rule assigns and those nobody answered (null: oldest admin). */
  leadManagerMembershipId: z.uuid().nullable(),
  businessHours: businessHoursSchema,
});
export type LeadSettingsInput = z.infer<typeof leadSettingsSchema>;

export const LEAD_OPERATIONS = {
  purchase: 'Compra',
  rent: 'Alquiler',
  listing: 'Captación',
} as const;
export type LeadOperation = keyof typeof LEAD_OPERATIONS;
export const DEFAULT_RESPONSE_MINUTES = 15;

/** A rule: the first active one matching the lead (channel and operation) assigns it. */
export const leadRoutingRuleSchema = z.strictObject({
  name: z.string().trim().min(1, 'Ponle un nombre').max(80),
  /** null: any channel. */
  channelId: z.uuid().nullable(),
  /** null: any operation. */
  operation: z.enum(Object.keys(LEAD_OPERATIONS) as [LeadOperation]).nullable(),
  responseMinutes: z.coerce
    .number()
    .int()
    .min(1, 'Al menos 1 minuto')
    .max(1440, 'Como máximo 1440 minutos (un día)'),
  active: z.boolean(),
  /** In rotation order. */
  membershipIds: z
    .array(z.uuid())
    .min(1, 'Elige al menos un agente')
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, 'Un agente aparece dos veces'),
});
export type LeadRoutingRuleInput = z.infer<typeof leadRoutingRuleSchema>;

/** PUT /lead-routing-rules/order: the rules' ids in priority order. */
export const leadRoutingOrderSchema = z.strictObject({
  ids: z.array(z.uuid()).min(1).max(100),
});

// ---------------------------------------------------------------------------
// Webhooks (HU-WEB-14): the ERP tells other systems that something happened
// ---------------------------------------------------------------------------

/** Events a webhook may subscribe to; payloads carry ids and public codes only. */
export const WEBHOOK_EVENTS = {
  'lead.created': {
    label: 'Lead recibido',
    help: 'Llega un lead por la API (id del lead y del negocio).',
  },
  'property.published': {
    label: 'Propiedad publicada en la web',
    help: 'Aparece en el catálogo público (su código).',
  },
  'property.updated': {
    label: 'Propiedad publicada actualizada',
    help: 'Cambia algo que muestra el sitio: textos, precio, fotos… (su código).',
  },
  'property.unpublished': {
    label: 'Propiedad retirada de la web',
    help: 'Deja el catálogo público: el sitio debe quitarla (su código).',
  },
  'deal.won': {
    label: 'Negocio ganado',
    help: 'Id del negocio, operación y códigos de sus propiedades.',
  },
} as const;
export type WebhookEventKey = keyof typeof WEBHOOK_EVENTS;

/** How many times a delivery is tried (the first try and the retries). */
export const WEBHOOK_MAX_ATTEMPTS = 8;

const webhookUrl = z
  .url({ protocol: /^https?$/, message: 'Escribe una dirección completa (https://…)' })
  .max(500)
  .refine((value) => {
    const url = new URL(value);
    return !url.username && !url.password && !url.hash;
  }, 'La dirección no puede llevar usuario, contraseña ni «#»');

export const webhookCreateSchema = z.strictObject({
  url: webhookUrl,
  description: z
    .string()
    .trim()
    .max(80)
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
  events: z
    .array(z.enum(Object.keys(WEBHOOK_EVENTS) as [WebhookEventKey]))
    .min(1, 'Elige al menos un evento')
    .transform((events) => [...new Set(events)]),
});
export type WebhookCreateInput = z.output<typeof webhookCreateSchema>;

export const webhookUpdateSchema = webhookCreateSchema.extend({ active: z.boolean() });
export type WebhookUpdateInput = z.output<typeof webhookUpdateSchema>;
